// src/pages/projects/SettingsDrawer.jsx
import { useState } from "react";
import api from "@/api/axios";
import GatewayDrawer from "@/pages/projects/GatewayDrawer";

export default function SettingsDrawer({ project, onClose }) {
  const [form, setForm] = useState({
    name: project.name,
    description: project.description,
    callbacks: { ...project.callbacks },
    gateways: JSON.parse(JSON.stringify(project.gatewayConfigs || {})),
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingGateway, setEditingGateway] = useState(null);
  const [editingFields, setEditingFields] = useState({});
  const [editingBaseFields, setEditingBaseFields] = useState([]);

  const updateField = (path, value) => {
    setForm((prev) => {
      const copy = { ...prev };
      let ref = copy;
      const parts = path.split(".");

      while (parts.length > 1) {
        const p = parts.shift();
        ref[p] = { ...ref[p] };
        ref = ref[p];
      }

      ref[parts[0]] = value;
      return copy;
    });
  };

  const removeGateway = (gw) => {
    const copy = { ...form.gateways };
    delete copy[gw];
    setForm((prev) => ({ ...prev, gateways: copy }));
  };

  const openGatewayDrawer = (gw) => {
    const existing = form.gateways[gw] || { config: {}, enabled: true };
    setEditingGateway(gw);

    const baseFieldsMap = {
      payu: ["PAYU_MERCHANT_KEY", "PAYU_MERCHANT_SALT", "PAYU_BASE_URL"],
      cashfree: ["CASHFREE_APP_ID", "CASHFREE_SECRET_KEY", "CASHFREE_BASE_URL"],
      razorpay: ["RAZORPAY_TEST_API_KEY", "RAZORPAY_TEST_API_SECRET"],
      paytm: ["PAYTM_MID", "PAYTM_MERCHANT_KEY", "PAYTM_MERCHANT_WEBSITE"],
      paypal: ["PAYPAL_CLIENT_ID", "PAYPAL_SECRET", "PAYPAL_BASE_URL"],
    };

    const gatewayKeyLower = gw.toLowerCase();
    const baseFields = baseFieldsMap[gatewayKeyLower] || [];

    const fields = {};
    (existing.config ? Object.keys(existing.config) : baseFields).forEach((k) => {
      fields[k] = existing.config?.[k] || "";
    });

    setEditingFields(fields);
    setEditingBaseFields(baseFields);
    setDrawerOpen(true);
  };

  const handleGatewayFieldChange = (k, v) => {
    setEditingFields((prev) => ({ ...prev, [k]: v }));
  };

  const markConfigured = (gatewayKey) => {
    setForm((prev) => {
      const copy = { ...prev };
      copy.gateways = {
        ...copy.gateways,
        [gatewayKey]: { enabled: true, config: { ...editingFields } },
      };
      return copy;
    });
  };

  const addGatewayFromSelect = (gw) => {
    if (!gw) return;
    openGatewayDrawer(gw);
  };

  const save = async () => {
    try {
      await api.patch(`/api/projects/${project._id}/settings`, form);
      alert("Settings updated");
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Could not save settings");
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
        <div className="max-w-[800px] mx-auto p-8 space-y-6">
          <div className="flex justify-between items-center border-b pb-4">
            <h2 className="text-2xl font-bold">Business Settings</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-black text-2xl"
            >
              ×
            </button>
          </div>

          <div>
            <label className="block mb-1 font-semibold">Business Name</label>
            <input
              className="border p-2 rounded w-full"
              value={form.name}
              onChange={(e) => updateField("name", e.target.value)}
            />

            <label className="block mt-3 mb-1 font-semibold">Description</label>
            <textarea
              className="border p-2 rounded w-full"
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold">Callback URLs</h3>

            {["successUrl", "failureUrl", "webhookUrl"].map((field) => (
              <div key={field}>
                <label className="block text-sm font-medium">{field}</label>
                <input
                  className="border p-2 rounded w-full"
                  value={form.callbacks[field] || ""}
                  onChange={(e) =>
                    updateField(`callbacks.${field}`, e.target.value)
                  }
                />
              </div>
            ))}
          </div>

          <div>
            <h3 className="font-semibold mb-2">Gateways</h3>

            {Object.entries(form.gateways).map(([gw, data]) => (
              <div
                key={gw}
                className="border rounded p-4 mb-3 bg-white shadow-sm"
              >
                <div className="flex justify-between items-center">
                  <h4 className="font-bold text-lg">{gw.toUpperCase()}</h4>

                  <div className="flex items-center gap-3">
                    <button
                      className="text-sm text-indigo-600"
                      onClick={() => openGatewayDrawer(gw)}
                    >
                      Edit
                    </button>

                    <button
                      className="text-red-600"
                      onClick={() => removeGateway(gw)}
                    >
                      Remove
                    </button>
                  </div>
                </div>

                <div className="mt-2">
                  <label className="font-medium">Enabled</label>
                  <input
                    type="checkbox"
                    className="ml-2"
                    checked={data.enabled}
                    onChange={(e) =>
                      updateField(`gateways.${gw}.enabled`, e.target.checked)
                    }
                  />
                </div>

                <div className="mt-3 space-y-2">
                  {Object.entries(data.config || {}).map(([k, v]) => (
                    <div key={k}>
                      <label className="text-sm">{k}</label>
                      <input
                        className="border p-2 rounded w-full"
                        value={v || ""}
                        onChange={(e) =>
                          updateField(
                            `gateways.${gw}.config.${k}`,
                            e.target.value
                          )
                        }
                      />
                    </div>
                  ))}

                  <button
                    className="mt-2 text-blue-600"
                    onClick={() => {
                      const key = prompt("Enter config field name:");
                      if (key) {
                        updateField(`gateways.${gw}.config.${key}`, "");
                      }
                    }}
                  >
                    + Add Field
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div>
            <select
              className="border p-2 rounded"
              onChange={(e) => addGatewayFromSelect(e.target.value)}
            >
              <option value="">Add Gateway...</option>
              <option value="payu">PayU</option>
              <option value="cashfree">Cashfree</option>
              <option value="razorpay">Razorpay</option>
              <option value="paytm">PayTM</option>
              <option value="paypal">PayPal</option>
            </select>
          </div>

          <div className="flex gap-3">
            <button
              onClick={save}
              className="bg-black text-white px-5 py-2 rounded-lg flex-1"
            >
              Save Settings
            </button>
            <button
              onClick={onClose}
              className="border border-gray-300 px-5 py-2 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {drawerOpen && (
        <GatewayDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          gatewayKey={editingGateway}
          gatewayLabel={editingGateway?.toUpperCase()}
          fields={editingFields}
          baseFields={editingBaseFields}
          onChange={(k, v) => handleGatewayFieldChange(k, v)}
          markConfigured={() => {
            markConfigured(editingGateway);
            setDrawerOpen(false);
          }}
        />
      )}
    </>
  );
}